/**
 * The role's blocked slots, in the panel section — where blockers are managed.
 *
 * A ticked checkbox means "this slot is blocked on the panel's calendars". It is
 * not a selection: unticking DELETES the calendar hold, so it asks for
 * confirmation first (the parent opens the modal). Holds are otherwise released
 * only when an interview is confirmed.
 *
 * The state column is the point of this list. A declined slot is free to offer
 * again while its hold stays put — so the recruiter can see, at a glance, which
 * times are theirs to re-offer without re-checking any calendar.
 */
import { Checkbox } from '@finn-auto/design-system/atoms/checkbox';
import { Button } from '@finn-auto/design-system/atoms/button';
import type { SlotHold } from '../../types/taActionBoard';
import { cn } from '../../../../shared';
import { fmtSlotRange } from './format';

const STATE_LABEL: Record<SlotHold['state'], string> = {
  held: 'on hold · not proposed yet',
  offered: 'proposed',
  declined: 'declined · free to propose again',
};

const STATE_CLASS: Record<SlotHold['state'], string> = {
  held: 'bg-gray-100 text-gray-600',
  offered: 'bg-blue-50 text-blue-700',
  declined: 'bg-[#FCEEE2] text-[#854F0B]',
};

export function HoldOverview({
  holds,
  legacyCount,
  onRelease,
  onReleaseLegacy,
}: {
  holds: SlotHold[];
  /** Holds from the previous shape, which no per-slot row can address */
  legacyCount: number;
  /** Unticking a slot — the caller confirms before anything is deleted */
  onRelease: (hold: SlotHold) => void;
  onReleaseLegacy: () => void;
}) {
  const offered = holds.filter((h) => h.state === 'offered').length;
  const reusable = holds.filter((h) => h.state !== 'offered').length;

  return (
    <div className="mt-2 rounded-md bg-white p-2.5 ring-1 ring-gray-200">
      <div className="flex flex-wrap items-baseline gap-x-2 gap-y-1 text-xs">
        <span className="font-semibold text-gray-800">Holds</span>
        {holds.length === 0 ? (
          <span className="text-gray-400">
            none — tick slots below and press “Hold selected slots”
          </span>
        ) : (
          <span className="text-gray-500">
            {holds.length} on the panel’s calendars · {offered} proposed ·{' '}
            {reusable} free to propose
          </span>
        )}
      </div>

      {holds.length > 0 && (
        <ul className="mt-1.5 space-y-1">
          {holds.map((hold) => (
            <li
              key={hold.key}
              className="flex flex-wrap items-center gap-2 rounded bg-gray-50 px-2 py-1 text-xs text-gray-800"
            >
              <Checkbox
                checked
                onCheckedChange={() => onRelease(hold)}
                aria-label={`Release the calendar hold on ${fmtSlotRange(hold)}`}
              />
              <span className="font-semibold">{fmtSlotRange(hold)}</span>
              <span className={cn('rounded-full px-2 py-0.5 font-medium', STATE_CLASS[hold.state])}>
                {STATE_LABEL[hold.state]}
              </span>
              {hold.offeredTo && (
                <span className="truncate text-gray-500">
                  {hold.state === 'declined' ? 'declined by ' : 'to '}
                  {hold.offeredTo.candidateName || 'a candidate'}
                </span>
              )}
            </li>
          ))}
        </ul>
      )}

      {legacyCount > 0 && (
        <div className="mt-1.5 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-1.5 text-xs text-gray-500">
          {/* These were created before holds were recorded per slot, so no row
              above can address them. Draining them is the only way they leave
              the panelists' calendars. */}
          <span>
            {legacyCount} hold{legacyCount === 1 ? '' : 's'} from an earlier version — not listed
            above, and not linked to any candidate.
          </span>
          <Button size="sm" variant="ghost" onClick={onReleaseLegacy}>
            Release them
          </Button>
        </div>
      )}
    </div>
  );
}
